import { useCallback, useRef } from 'react';

import { cfetch } from '@confluence/network';
import { throwErrorWithResponse } from '@confluence/graphql/entry-points/throwErrorWithResponse';
import type { BadStatusError } from '@confluence/network';

// Allows using variables from either the mutate call or initial hook call
const resolveVariables = <TVariables>(
	localVariables: TVariables,
	fallbackVariables: TVariables,
): TVariables => {
	return localVariables && Object.keys(localVariables).length > 0
		? localVariables
		: fallbackVariables;
};

export type FetchOptions<TVariables, TResponse> = {
	url: string | ((variables: TVariables) => string);
	method?: string;
	headers?: HeadersInit;
	body?: string | ((variables: TVariables) => string);
	credentials?: RequestCredentials;
	parseResponseOnSuccess?: (response: Response) => TResponse | Promise<TResponse>;
	parseResponseOnError?: (
		error: Error | BadStatusError,
		variables: TVariables,
	) => TResponse | Promise<TResponse>;
};

export const useFetchData = <TData, TVariables, TResponse>({
	url,
	method,
	variables = {} as TVariables,
	headers,
	body,
	credentials,
	parseResult,
}: FetchOptions<TVariables, TResponse> & {
	variables?: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
}) => {
	// Store props in refs to access the latest values within `useCallback` without adding them to the dependency array
	// This ensures the `fetchData` function remains stable across re-renders
	const latestUrl = useRef(url);
	latestUrl.current = url;

	const latestHeaders = useRef(headers);
	latestHeaders.current = headers;

	const latestBody = useRef(body);
	latestBody.current = body;

	const latestParseResult = useRef(parseResult);
	latestParseResult.current = parseResult;

	const latestVariables = useRef(variables);
	latestVariables.current = variables;

	const latestCredentials = useRef(credentials);
	latestCredentials.current = credentials;

	const fetchData = useCallback(
		async (localVariables = {} as TVariables): Promise<TData> => {
			const variablesToUse = resolveVariables(localVariables, latestVariables.current);

			const response = await fetchResponse<TVariables, TResponse>(
				{
					url: latestUrl.current,
					method,
					body: latestBody.current,
					headers: latestHeaders.current,
					credentials: latestCredentials.current,
				},
				variablesToUse,
			);

			return latestParseResult.current(response, variablesToUse);
		},
		[method],
	);

	return { fetchData };
};

// Fetch multiple endpoints and parse their responses
export const useFetchDataWithMultipleFetches = <TData, TVariables, TResponse>({
	requestConfigs,
	variables = {} as TVariables,
	parseResult,
}: {
	requestConfigs: FetchOptions<TVariables, TResponse>[];
	variables?: TVariables;
	parseResult: (responses: TResponse[], variables: TVariables) => TData | Promise<TData>;
}) => {
	const latestRequestConfigs = useRef(requestConfigs);
	latestRequestConfigs.current = requestConfigs;

	const latestParseResult = useRef(parseResult);
	latestParseResult.current = parseResult;

	const latestVariables = useRef(variables);
	latestVariables.current = variables;

	const fetchData = useCallback(async (localVariables = {} as TVariables): Promise<TData> => {
		const variablesToUse = resolveVariables(localVariables, latestVariables.current);

		const responses = await Promise.all(
			latestRequestConfigs.current.map((fetchOptions) =>
				fetchResponse<TVariables, TResponse>(fetchOptions, variablesToUse),
			),
		);

		return latestParseResult.current(responses, variablesToUse);
	}, []);

	return { fetchData };
};

export async function fetchResponse<TVariables, TResponse>(
	fetchOptions: FetchOptions<TVariables, TResponse>,
	variables: TVariables,
): Promise<TResponse> {
	const { url, method, body, headers, credentials, parseResponseOnSuccess, parseResponseOnError } =
		fetchOptions;
	try {
		const defaultBody = method === 'GET' ? undefined : JSON.stringify(variables);

		const response = await cfetch(typeof url === 'function' ? url(variables) : url, {
			method,
			body: typeof body === 'function' ? body(variables) : body || defaultBody,
			headers,
			credentials,
		});

		if (parseResponseOnSuccess) {
			return parseResponseOnSuccess(response);
		}

		return response.status === 204
			? // @ts-expect-error - TS2322 Typescript 5.9.2 upgrade
				{}
			: await response.json().catch((error) => {
					throw throwErrorWithResponse(error, response);
				});
	} catch (error) {
		if (parseResponseOnError) {
			return parseResponseOnError(error, variables);
		}
		throw error;
	}
}
